import { useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  IconButton,
  InputAdornment,
  TableHead,
  TextField as MuiTextField,
  Box,
  Select,
  MenuItem,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Datagrid, Identifier, Loading, useRecordContext, useTranslate, useRedirect,
} from 'react-admin';
import { SecurityInventoryCategory, SecurityInventoryItem, Asset } from '@x-guard/xgac-types/xgac';
import { useGetList } from 'ra-core';
import { MapContainer, ScaleControl, TileLayer } from 'react-leaflet';
import { InventoryCategoryIconName } from '../components/icons/inventoryCategoryIcons';
import 'leaflet/dist/leaflet.css';
import { IconField } from '../components/fields/IconField';

const Grid = Grid2;
const securityItemsResource = 'security-inventory-items';
const securityInventoryCategoriesResource = 'security-inventory-categories';
const appUsersResource = 'app-users';

const CreateItemSection = (props: { categories: SecurityInventoryCategory[] }) => {

  const translate = useTranslate();

  const [showCategories, setShowCategories] = useState(false);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1, width: '100%',
    }}>
      <button
        type="button"
        className="add-device-button"
        onClick={() => setShowCategories((open) => !open)}
        style={{
          width: '100%',
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <span>{translate('resources.security-inventory-items.text.add_item')}</span>
      </button>
      {showCategories && (
        <Select
          size="small"
          fullWidth
          displayEmpty
          value=""
          onChange={() => setShowCategories(false)}
          renderValue={() => <span style={{ color: '#888' }}>{translate('resources.security-inventory-categories.text.select_category')}</span>}
        >
          {props.categories.map((category) => (
            <MenuItem key={category._id} value={category._id}>
              <IconField iconName={category.icon as InventoryCategoryIconName} source={category.icon} />
              <span style={{ marginLeft: '10px' }}></span>
              {category.name}
            </MenuItem>
          ))}
        </Select>
      )}

    </Box>
  );

};

export const InventoryItemsMap = () => (
  <MapContainer
    center={[52.1009166, 5.6462914]}
    zoom={17}
    id="inventory-items-map-container"
    minZoom={4}
    zoomControl
    style={{ height: '100%', width: '100%' }}
  >
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    />
    <ScaleControl position="bottomright" imperial={false} />
  </MapContainer>
);

const SearchInput = (props: { searchTerm: string; setSearchTerm: React.Dispatch<React.SetStateAction<string>> }) => {

  const translate = useTranslate();

  return (
    <MuiTextField
      className="sidebar-search-input"
      label=""
      placeholder={translate('resources.security-inventory-items.text.search')}
      variant="outlined"
      color="info"
      fullWidth
      value={props.searchTerm}
      onChange={(e) => props.setSearchTerm(e.target.value)}
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
        sx: {
          height: '35px',
          borderRadius: '3px',
          borderColor: '#9AB4A3',
          fontSize: '12px',
          '& .MuiSvgIcon-root': {
            color: 'black',
          },
        },
      }}
      inputProps={{
        style: { height: '40px' },
      }}
    />
  );

};

const FilterSelector = (props: {
  label: string;
  items: { name: string; _id: string; icon?: InventoryCategoryIconName }[];
  value: string | null;
  onChange: (value: string | null) => void;
}) => {

  const translate = useTranslate();

  return (
    <Box>
      <span className="device-filter-row-label" style={{ display: 'block', marginBottom: 4 }}>
        {props.label}
      </span>
      <Select
        displayEmpty
        fullWidth
        size="small"
        value={props.value ?? ''}
        onChange={(e) => {

          const v = e.target.value as string;
          props.onChange(v === '' ? null : v);

        }}
        sx={{
          height: 35,
          fontSize: 12,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9AB4A3',
          },
        }}
        renderValue={(selected) => {

          if (!selected) {

            return <span style={{ color: '#888' }}>{props.label}</span>;

          }
          return props.items.find((c) => c._id === selected)?.name ?? selected;

        }}
      >
        <MenuItem value="">
          <em>{translate('general.text.all')}</em>
        </MenuItem>
        {props.items.map((item) => (
          <MenuItem key={item._id} value={item._id}>
            {item.icon
            && <>
              <IconField iconName={item.icon as InventoryCategoryIconName} source={item.icon} />
              <span style={{ marginLeft: '10px' }}></span>
            </>
            }
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

};

const FilterButton = (props: {
  children: React.ReactNode;
  categories: SecurityInventoryCategory[];
  selectedCategory: string | null;
  setSelectedCategory: React.Dispatch<React.SetStateAction<string | null>>;
  users: Asset[];
  selectedUser: string | null;
  setSelectedUser: React.Dispatch<React.SetStateAction<string | null>>;
}) => {

  const translate = useTranslate();
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftCategory, setDraftCategory] = useState<string | null>(props.selectedCategory);
  const [draftUser, setDraftUser] = useState<string | null>(props.selectedUser);

  useEffect(() => {

    if (filterOpen) {

      setDraftCategory(props.selectedCategory);
      setDraftUser(props.selectedUser);

    }

  }, [filterOpen, props.selectedCategory, props.selectedUser]);

  const hasFilters = props.selectedCategory != null || props.selectedUser != null;

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
    }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
        {/* This ensures that the search bar and filter are in the same row, but
        when the filter is opened its pop-up is full-width */}
        <Box sx={{ flex: '1 1 60%', minWidth: 0 }}>
          {props.children}
        </Box>
        <Box sx={{ flex: '0 0 40%', minWidth: 0 }}>
          <button
            type="button"
            className={hasFilters ? 'filter-items-button filter-items-button-active' : 'filter-items-button'}
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <TuneIcon />
            <span>
              {translate(hasFilters ? 'general.text.filtered' : 'general.text.filter')}
            </span>
          </button>
        </Box>
      </Box>
      {filterOpen && (
        <div className="device-filter-div">
          <div className="device-filter-div-inner">
            <Box sx={{ px: 1.25, py: 1.25 }}>
              <FilterSelector
                label={translate('resources.security-inventory-categories.name', { smart_count: 1, _: 'Category' })}
                items={props.categories.map((category) => ({
                  name: category.name,
                  _id: category._id,
                  icon: category.icon as InventoryCategoryIconName,
                }))}
                value={draftCategory}
                onChange={setDraftCategory}
              />
              <FilterSelector
                label={translate('resources.app-users.name', { smart_count: 1, _: 'User' })}
                items={props.users.map((user) => ({
                  name: user.name,
                  _id: user._id,
                }))}
                value={draftUser}
                onChange={setDraftUser}
              />
            </Box>
            <div className="device-filter-buttons">
              <button
                type="button"
                onClick={() => {

                  setDraftCategory(null);
                  setDraftUser(null);
                  props.setSelectedCategory(null);
                  props.setSelectedUser(null);
                  setFilterOpen(false);

                }}
              >
                {translate('resources.buttons.text.remove_filters')}
              </button>
              <button
                type="button"
                onClick={() => {

                  props.setSelectedCategory(draftCategory);
                  props.setSelectedUser(draftUser);
                  setFilterOpen(false);

                }}
              >
                {translate('resources.buttons.text.save_filters')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );

};

const BulkActions = () => {

  const translate = useTranslate();

  return (
    <Grid container display="flex" flexDirection="row" justifyContent="flex-end">
      <Grid xs={3} >
        <button type="button" className="device-action-button" onClick={() => {}}>
          <DeleteIcon />
          <span>{translate('ra.action.delete')}</span>
        </button>
      </Grid>
    </Grid>
  );

};

const ItemCategorySettingsButton = () => {

  const translate = useTranslate();
  const redirect = useRedirect();

  return (
    <button type="button" className="sidebar-item-category-settings-button" onClick={() => {

      redirect('list', 'security-inventory-categories');

    }}>
      <span>{translate('resources.security-inventory-categories.text.settings_button')}</span>
    </button>
  );

};

const SelectAllButton = (props: {
  selectedIds: Identifier[]; setSelectedIds: React.Dispatch<React.SetStateAction<Identifier[]>>; items: SecurityInventoryItem[];
}) => {

  const translate = useTranslate();
  const allSelected = (props.selectedIds.length === props.items.length) && (props.items.length > 0);

  return (
    <>
      {allSelected ? (
        <button type="button" className="select-all-button deselect-all-button" onClick={() => {

          props.setSelectedIds([]);

        }}>
          <span>{translate('general.text.deselect_all')}</span>
        </button>
      ) : (
        <>
          <button type="button" className="select-all-button" onClick={() => {

            props.setSelectedIds(props.items.map((item) => item._id));

          }}>
            <span>{translate('general.text.select_all')}</span>
          </button>
        </>
      )}
    </>
  );

};

const ItemDataGridListItem = (props: {
  mouseHoverId: Identifier | null;
  setMouseHoverId: React.Dispatch<React.SetStateAction<Identifier | null>>;
  selectedIds: Identifier[];
  setSelectedIds: React.Dispatch<React.SetStateAction<Identifier[]>>;
}) => {

  const record = useRecordContext();
  if (!record) return <></>;
  const id = record._id;
  const showIdleRow = props.mouseHoverId !== id && props.selectedIds.length === 0;

  return (
    <div
      onMouseEnter={() => props.setMouseHoverId(id)}
      onMouseLeave={() => props.setMouseHoverId(null)}
      style={{ cursor: 'pointer' }}
    >
      <div className={props.selectedIds.includes(id) ? 'button-sidebar-grid-row selected-row' : 'button-sidebar-grid-row'}>
        {showIdleRow ? (
          <>
            <div className="button-sidebar-grid-name">
              <span style={{
                display: 'inline-block',
                width: 15,
                flexShrink: 0,
              }}
              />
              <span style={{
                fontWeight: 'bold', marginLeft: '15px',
              }}
              >
                {record.name}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="button-sidebar-grid-name">
              <Checkbox
                checked={props.selectedIds.includes(id)}
                size="small"
                className="button-sidebar-checkbox"
                onClick={(e) => {

                  e.stopPropagation();
                  if (props.selectedIds.includes(id)) {

                    props.setSelectedIds(props.selectedIds.filter((selectedId) => selectedId !== id));

                  } else {

                    props.setSelectedIds([...props.selectedIds, id]);

                  }

                }}
              />
              <span style={{
                fontWeight: 'bold', marginLeft: '15px',
              }}
              >
                {record.name}
              </span>
            </div>
            {props.mouseHoverId === id ? (
              <IconButton size="small" sx={{ padding: 0, margin: 0 }}>
                <EditIcon sx={{ padding: 0, margin: 0, fontSize: '20px' }} />
              </IconButton>
            ) : (
              <div
                style={{
                  minWidth: 32,
                  flexShrink: 0,
                  alignSelf: 'center',
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );

};

export const InventoryItemsSidebar = () => {

  const translate = useTranslate();
  const [mouseHoverId, setMouseHoverId] = useState<Identifier | null>(null);
  const [selectedIds, setSelectedIds] = useState<Identifier[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const showBulkActions = selectedIds.length > 0;

  // For all of the fetched resources, all of the data is fetched at once without pagination
  const { data: itemsData, isLoading } = useGetList(securityItemsResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const { data: categoriesData } = useGetList(securityInventoryCategoriesResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  const { data: usersData } = useGetList(appUsersResource, {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  });

  // Both search and filter are applied together
  // Note: the categoriesData is used to check if the selected user is in the category's peopleToNotify array
  const filteredItems = useMemo(() => {

    const list = itemsData ?? [];
    const q = searchTerm.trim().toLowerCase();

    // Map is used for faster lookup of peopleToNotify by categoryId
    const peopleToNotifyByCategoryId = new Map<string, Set<string>>();
    for (const category of categoriesData ?? []) {

      peopleToNotifyByCategoryId.set(
        category._id,
        new Set(category.peopleToNotify ?? []),
      );

    }
    return list.filter((item) => {

      const categoryId = item.category._id;

      if (selectedCategory && categoryId !== selectedCategory) return false;
      if (selectedUser) {

        const peopleToNotifySet = peopleToNotifyByCategoryId.get(categoryId);
        if (!peopleToNotifySet?.has(selectedUser)) return false;

      }

      if (q && !(item.name ?? '').toLowerCase().includes(q)) return false;
      return true;

    });

  }, [itemsData, selectedCategory, selectedUser, searchTerm, categoriesData]);

  return (
    <div className="button-sidebar-div">
      <div className="button-sidebar-add">
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <ItemCategorySettingsButton />
        </Box>
        <h4>
          <span>{showBulkActions ? translate('resources.buttons.text.actions_with_selected') : translate('menu.items.security_inventory')}</span>
        </h4>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showBulkActions && <BulkActions />}
          {!showBulkActions && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <CreateItemSection categories={categoriesData || []} />
            <FilterButton
              categories={categoriesData || []}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              users={usersData || []}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}>
              <SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </FilterButton>
          </Box>}
          <SelectAllButton selectedIds={selectedIds} setSelectedIds={setSelectedIds} items={itemsData || []} />
        </Box>
      </div>
      <div className="button-sidebar-grid">
        {isLoading ? (
          <Loading loadingPrimary={translate('general.text.loading')} loadingSecondary={translate('general.text.loading_secondary')} />
        ) : (
          <Datagrid
            resource={securityItemsResource}
            data={filteredItems}
            total={filteredItems.length}
            empty={
              <Box sx={{
                px: 2, py: 3, textAlign: 'center', color: 'text.secondary', fontSize: 14,
              }}>
                {translate('resources.security-inventory-items.text.no_results')}
              </Box>
            }
            isLoading={isLoading}
            bulkActionButtons={false}
            rowClick={false}
            header={<TableHead />}
            rowStyle={(record: SecurityInventoryItem, index: number) => {

              if (selectedIds.includes(record._id)) {

                return {
                  backgroundColor: '#9FD2B6',
                };

              }
              return {
                backgroundColor: index % 2 === 0 ? '#F0F9F5' : '#E2F1EA',
              };

            }}
            sx={{
              '& .RaDatagrid-expandIconCell': {
                display: 'none',
              },
            }}
          >
            <ItemDataGridListItem mouseHoverId={mouseHoverId} setMouseHoverId={setMouseHoverId} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
          </Datagrid>
        )}
      </div>
    </div>
  );

};

// This provides the layout of the page and map and sidebar components are passed in as props
const InventoryItemsLayout = ({ map, sidebar }: { map: React.ReactNode; sidebar: React.ReactNode }) => (
  <Grid container sx={{ height: 'calc(100vh - 64px)' }}>
    <Grid xs={9} md={9} sx={{ minHeight: 0 }}>
      {map}
    </Grid>
    <Grid xs={3} md={3} sx={{
      minHeight: 0, display: 'flex', flexDirection: 'column', borderLeft: 1, borderColor: 'divider', overflow: 'hidden',
    }}
    >
      {sidebar}
    </Grid>
  </Grid>
);

export const InventoryItemsList = () => (

  <InventoryItemsLayout map={<InventoryItemsMap />} sidebar={<InventoryItemsSidebar />} />

);
